import { useCallback, useState } from "react";
import { type UseFormReturn, Controller } from "react-hook-form";
import { Plus, X, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { DatePicker } from "@/components/ui/DatePicker";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import type { IntakeFormData, DropdownConfig } from "../hooks/useIntakeForm";

interface DemographicsStepProps {
  form: UseFormReturn<IntakeFormData>;
  dropdowns: DropdownConfig | undefined;
  isLoadingDropdowns: boolean;
  ssnDuplicateWarning: string | null;
  onSsnBlur: (ssn: string) => Promise<void>;
  onZipBlur: (zip: string) => Promise<void>;
  personType: string;
}

export function DemographicsStep({
  form,
  dropdowns,
  isLoadingDropdowns,
  ssnDuplicateWarning,
  onSsnBlur,
  onZipBlur,
  personType,
}: DemographicsStepProps) {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const [showSsn, setShowSsn] = useState(false);
  const [aliases, setAliases] = useState<string[]>(watch("aliases") ?? []);
  const [newAlias, setNewAlias] = useState("");

  const addAlias = useCallback(() => {
    if (!newAlias.trim()) return;
    const updated = [...aliases, newAlias.trim()];
    setAliases(updated);
    setValue("aliases", updated);
    setNewAlias("");
  }, [aliases, newAlias, setValue]);

  const removeAlias = useCallback(
    (idx: number) => {
      const updated = aliases.filter((_, i) => i !== idx);
      setAliases(updated);
      setValue("aliases", updated);
    },
    [aliases, setValue],
  );

  const isChild = personType === "child";

  if (isLoadingDropdowns) {
    return <DemographicsStepSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Name & Identity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Identity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Input
              label="First Name"
              required
              error={errors.firstName?.message}
              {...register("firstName")}
            />
            <Input
              label="Last Name"
              required
              error={errors.lastName?.message}
              {...register("lastName")}
            />
            <Input
              label="Middle Name"
              error={errors.middleName?.message}
              {...register("middleName")}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input
              label="Preferred Name"
              error={errors.preferredName?.message}
              {...register("preferredName")}
            />
            <Input
              label="ID Number"
              placeholder="Auto-generated if blank"
              error={errors.idNumber?.message}
              {...register("idNumber")}
            />
          </div>

          {/* Aliases */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">
              Alias Names
            </label>
            <div className="flex gap-2">
              <input
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addAlias();
                  }
                }}
                placeholder="Add alias name..."
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <Button type="button" variant="outline" size="icon" onClick={addAlias}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {aliases.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {aliases.map((alias, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground"
                  >
                    {alias}
                    <button
                      type="button"
                      onClick={() => removeAlias(idx)}
                      className="rounded-full p-0.5 hover:bg-secondary-foreground/10"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* SSN */}
          {personType !== "hotline" && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  SSN
                </label>
                <div className="relative">
                  <input
                    type={showSsn ? "text" : "password"}
                    placeholder="XXX-XX-XXXX"
                    maxLength={11}
                    {...register("ssn", {
                      onBlur: (e) => {
                        const val = e.target.value;
                        if (val) void onSsnBlur(val);
                      },
                    })}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 pr-9 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSsn(!showSsn)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showSsn ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {errors.ssn?.message && (
                  <p className="text-xs text-destructive">{errors.ssn.message}</p>
                )}
                {ssnDuplicateWarning && (
                  <p className="text-xs text-amber-600">{ssnDuplicateWarning}</p>
                )}
              </div>

              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Date of Birth"
                    showAge
                    max={new Date().toISOString().slice(0, 10)}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={errors.dateOfBirth?.message}
                    required={personType === "shelter" || personType === "child"}
                  />
                )}
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <Select
                  label="Gender"
                  options={dropdowns?.genders ?? []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.gender?.message}
                  searchable
                  clearable
                  loading={isLoadingDropdowns}
                  required={personType === "shelter" || personType === "outreach"}
                />
              )}
            />
            <Controller
              name="race"
              control={control}
              render={({ field }) => (
                <Select
                  label="Race"
                  options={dropdowns?.races ?? []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.race?.message}
                  searchable
                  clearable
                  loading={isLoadingDropdowns}
                />
              )}
            />
            <Controller
              name="ethnicity"
              control={control}
              render={({ field }) => (
                <Select
                  label="Ethnicity"
                  options={dropdowns?.ethnicities ?? []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.ethnicity?.message}
                  clearable
                  loading={isLoadingDropdowns}
                />
              )}
            />
          </div>

          {!isChild && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Controller
                name="maritalStatus"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Marital Status"
                    options={dropdowns?.maritalStatuses ?? []}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    clearable
                    loading={isLoadingDropdowns}
                  />
                )}
              />
              <Controller
                name="educationLevel"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Education Level"
                    options={dropdowns?.educationLevels ?? []}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    clearable
                    loading={isLoadingDropdowns}
                  />
                )}
              />
              <Controller
                name="primaryLanguage"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Primary Language"
                    options={dropdowns?.languages ?? []}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    searchable
                    clearable
                    loading={isLoadingDropdowns}
                  />
                )}
              />
            </div>
          )}

          {isChild && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Controller
                name="primaryLanguage"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Primary Language"
                    options={dropdowns?.languages ?? []}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    searchable
                    clearable
                    loading={isLoadingDropdowns}
                  />
                )}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Intake Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Intake Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Controller
              name="intakeDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Intake Date"
                  required
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={errors.intakeDate?.message}
                />
              )}
            />
            <Input
              type="time"
              label="Intake Time"
              error={errors.intakeTime?.message}
              {...register("intakeTime")}
            />
            <Controller
              name="siteId"
              control={control}
              render={({ field }) => (
                <Select
                  label="Service Site"
                  options={dropdowns?.sites ?? []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  clearable
                  loading={isLoadingDropdowns}
                />
              )}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Controller
              name="programId"
              control={control}
              render={({ field }) => (
                <Select
                  label="Program Name"
                  options={dropdowns?.programs ?? []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  clearable
                  loading={isLoadingDropdowns}
                />
              )}
            />
            <Controller
              name="advocateId"
              control={control}
              render={({ field }) => (
                <Select
                  label="Advocate/Staff"
                  options={dropdowns?.advocates ?? []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  searchable
                  clearable
                  loading={isLoadingDropdowns}
                />
              )}
            />
            <Controller
              name="referralSource"
              control={control}
              render={({ field }) => (
                <Select
                  label="Referred By"
                  options={dropdowns?.referralSources ?? []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  searchable
                  clearable
                  loading={isLoadingDropdowns}
                />
              )}
            />
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Address</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Input
                label="Street Address"
                error={errors.address?.message}
                {...register("address")}
              />
            </div>
            <Input
              label="Apt/Suite"
              error={errors.addressApt?.message}
              {...register("addressApt")}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Input
              label="ZIP Code"
              maxLength={10}
              error={errors.zipCode?.message}
              {...register("zipCode", {
                onBlur: (e) => {
                  const val = e.target.value;
                  if (val && val.length >= 5) void onZipBlur(val);
                },
              })}
            />
            <Input
              label="City"
              error={errors.city?.message}
              {...register("city")}
            />
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <Select
                  label="State"
                  options={dropdowns?.usStates ?? []}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  searchable
                  clearable
                  error={errors.state?.message}
                  loading={isLoadingDropdowns}
                />
              )}
            />
            <Input
              label="County"
              error={errors.county?.message}
              {...register("county")}
            />
          </div>
        </CardContent>
      </Card>

      {/* Phone Numbers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Phone Numbers</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="phoneHome"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  label="Home Phone"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  showPrimary
                  isPrimary={watch("primaryPhone") === "home"}
                  onPrimaryChange={() => setValue("primaryPhone", "home")}
                  showSafe
                  isSafe={watch("safePhoneHome")}
                  onSafeChange={(v) => setValue("safePhoneHome", v)}
                />
              )}
            />
            <Controller
              name="phoneWork"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  label="Work Phone"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  showPrimary
                  isPrimary={watch("primaryPhone") === "work"}
                  onPrimaryChange={() => setValue("primaryPhone", "work")}
                  showSafe
                  isSafe={watch("safePhoneWork")}
                  onSafeChange={(v) => setValue("safePhoneWork", v)}
                />
              )}
            />
            <Controller
              name="phoneMobile"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  label="Mobile Phone"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  showPrimary
                  isPrimary={watch("primaryPhone") === "mobile"}
                  onPrimaryChange={() => setValue("primaryPhone", "mobile")}
                  showSafe
                  isSafe={watch("safePhoneMobile")}
                  onSafeChange={(v) => setValue("safePhoneMobile", v)}
                />
              )}
            />
            <Controller
              name="phoneEmergency"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  label="Emergency Phone"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  showPrimary
                  isPrimary={watch("primaryPhone") === "emergency"}
                  onPrimaryChange={() => setValue("primaryPhone", "emergency")}
                />
              )}
            />
          </div>
          <Input
            label="Email"
            type="email"
            error={errors.email?.message}
            {...register("email")}
          />
        </CardContent>
      </Card>

      {/* Victim & Housing (not for child) */}
      {!isChild && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Victim & Housing Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Controller
                name="victimType"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Victim Type"
                    options={dropdowns?.victimTypes ?? []}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    clearable
                    loading={isLoadingDropdowns}
                  />
                )}
              />
              <Controller
                name="housingStatus"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Housing Status"
                    options={dropdowns?.housingStatuses ?? []}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    clearable
                    loading={isLoadingDropdowns}
                  />
                )}
              />
              <Controller
                name="priorLivingSituation"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Prior Living Situation"
                    options={dropdowns?.housingStatuses ?? []}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    clearable
                    loading={isLoadingDropdowns}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Controller
                name="employmentStatus"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Work Status"
                    options={dropdowns?.employmentStatuses ?? []}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    clearable
                    loading={isLoadingDropdowns}
                  />
                )}
              />
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Monthly Income
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      $
                    </span>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      {...register("monthlyIncome", {
                        valueAsNumber: true,
                        onChange: (e) => {
                          const monthly = parseFloat(e.target.value);
                          if (!isNaN(monthly)) {
                            setValue("annualIncome", Math.round(monthly * 12));
                          }
                        },
                      })}
                      className="flex h-9 w-full rounded-md border border-input bg-background py-1 pl-7 pr-3 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      placeholder="0.00"
                    />
                  </div>
                  <span className="flex items-center text-xs text-muted-foreground">
                    /mo
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Annual Income
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    $
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    {...register("annualIncome", {
                      valueAsNumber: true,
                      onChange: (e) => {
                        const annual = parseFloat(e.target.value);
                        if (!isNaN(annual)) {
                          setValue("monthlyIncome", Math.round((annual / 12) * 100) / 100);
                        }
                      },
                    })}
                    className="flex h-9 w-full rounded-md border border-input bg-background py-1 pl-7 pr-3 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Controller
                name="familyType"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Family Type"
                    options={dropdowns?.familyTypes ?? []}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    clearable
                    loading={isLoadingDropdowns}
                  />
                )}
              />
              <Input
                label="Household Size"
                type="number"
                min={0}
                {...register("householdSize", { valueAsNumber: true })}
              />
              {personType === "shelter" && (
                <Input
                  label="# in Shelter"
                  type="number"
                  min={0}
                  {...register("numberInShelter", { valueAsNumber: true })}
                />
              )}
              <Controller
                name="urbanRural"
                control={control}
                render={({ field }) => (
                  <Select
                    label="Urban/Rural"
                    options={[
                      { value: "urban", label: "Urban" },
                      { value: "rural", label: "Rural" },
                      { value: "suburban", label: "Suburban" },
                    ]}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    clearable
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Child-specific fields */}
      {isChild && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Child Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label="Parent/Guardian Person ID"
              required
              placeholder="Select the parent/adult associated with this child"
              error={errors.parentPersonId?.message}
              {...register("parentPersonId")}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="School Name"
                {...register("schoolName")}
              />
              <Input
                label="Grade Level"
                {...register("gradeLevel")}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Pediatrician"
                {...register("pediatrician")}
              />
              <Input
                label="Custody Status"
                {...register("custodyStatus")}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function DemographicsStepSkeleton() {
  return (
    <div className="space-y-6">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i}>
          <CardHeader>
            <div className="h-5 w-32 animate-pulse rounded bg-muted" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((j) => (
                <div key={j} className="space-y-1.5">
                  <div className="h-4 w-20 animate-pulse rounded bg-muted" />
                  <div className="h-9 w-full animate-pulse rounded-md bg-muted" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
